import React, { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Box,
  CircularProgress,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useTranslation } from 'react-i18next';

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  branchId?: string | null;
  companyBranchName?: string | null;
  subAccounts?: { [key: string]: SubAccount };
}

// Alt hesap arayüzü
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  parentId: string;
}

interface BankAccountListWidgetProps {
  bankAccounts: BankAccount[];
  loading: boolean;
  getCurrencySymbol: (currency: string) => string;
  branches?: any[];
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  overflow: 'hidden',
  '& .MuiCardHeader-root': {
    padding: theme.spacing(1),
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 'bold',
    },
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1),
  },
}));

// Şube bazlı gruplama tipi
interface BranchGroup {
  branchId: string | null;
  branchName: string;
  totalBalance: number;
  count: number;
  currency: string;
  items: BankAccount[]; // Gruptaki bireysel banka hesapları (detay modalı için)
}

const BankAccountListWidget: React.FC<BankAccountListWidgetProps> = ({
  bankAccounts,
  loading,
  getCurrencySymbol,
  branches = []
}) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  // Seçilen şube grubu (detay modalı)
  const [selectedGroup, setSelectedGroup] = useState<BranchGroup | null>(null);

  const formatBalance = (value: number, currency: string) =>
    `${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol(currency)}`;

  // Ana hesap için toplam bakiye hesapla (kendi bakiyesi + alt hesapların bakiyeleri)
  const getTotalBalance = (account: BankAccount): number => {
    let total = account.balance;
    
    if (account.subAccounts) {
      Object.values(account.subAccounts).forEach(subAccount => {
        total += subAccount.balance;
      });
    }
    
    return total;
  };

  // Banka hesaplarını şube bazlı grupla (şubesiz olanlar kendi adlarıyla listelenir)
  const branchGroups: BranchGroup[] = useMemo(() => {
    const groupMap = new Map<string, BranchGroup>();
    const noBranchItems: BranchGroup[] = [];

    bankAccounts.forEach(account => {
      const key = account.branchId || null;
      const totalBalance = getTotalBalance(account);

      if (!key) {
        // Şubeye bağlı değil → kendi adıyla ayrı satır olarak göster
        noBranchItems.push({
          branchId: null,
          branchName: account.bankName || t('dashboard.bankAccountsWidgetDefaultName'),
          totalBalance,
          count: 1,
          currency: account.currency || 'TRY',
          items: [account],
        });
        return;
      }

      const existing = groupMap.get(key);
      if (existing) {
        existing.totalBalance += totalBalance;
        existing.count += 1;
        existing.items.push(account);
      } else {
        const branchName = account.companyBranchName
          || branches.find((b: any) => (b.id || b.branchId) === key)?.name
          || key;

        groupMap.set(key, {
          branchId: key,
          branchName,
          totalBalance,
          count: 1,
          currency: account.currency || 'TRY',
          items: [account],
        });
      }
    });

    // Şube grupları + şubesiz bireysel hesaplar birleştir, bakiyeye göre sırala
    return [...Array.from(groupMap.values()), ...noBranchItems].sort((a, b) => b.totalBalance - a.totalBalance);
  }, [bankAccounts, branches, t]);

  return (
    <StyledCard>
      <CardHeader 
        title={t('dashboard.bankAccountsWidgetTitle')}
        titleTypographyProps={{ fontWeight: 'bold' }}
      />
      <Divider />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : branchGroups.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
            {t('dashboard.bankAccountsWidgetNoData')}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {branchGroups.map((group, index) => (
              <Box 
                key={group.branchId || `no-branch-${index}`}
                onClick={() => setSelectedGroup(group)}
                sx={{ 
                  p: 1.5, 
                  borderBottom: index < branchGroups.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    sx={{ maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {group.branchName}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    color={group.totalBalance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatBalance(group.totalBalance, group.currency)}
                  </Typography>
                </Box>
                {group.branchId && (
                  <Typography variant="caption" color="textSecondary">
                    {t('dashboard.bankAccountsWidgetCount', { count: group.count })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Şube banka hesapları detay modalı (alt hesaplar girintili) */}
      <Dialog
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color="warning" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {selectedGroup?.branchName}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelectedGroup(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedGroup?.items.map((account, idx) => {
            const subList = account.subAccounts ? Object.values(account.subAccounts) : [];
            return (
              <Box
                key={account.id || idx}
                sx={{
                  borderBottom: idx < (selectedGroup?.items.length || 0) - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                {/* Ana hesap */}
                <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {account.bankName || t('dashboard.bankAccountsWidgetDefaultName')}
                    </Typography>
                    {(account.accountNumber || account.iban) && (
                      <Typography variant="caption" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {account.accountNumber || account.iban}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={account.balance >= 0 ? 'success.main' : 'error.main'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {formatBalance(account.balance, account.currency || 'TRY')}
                  </Typography>
                </Box>
                {/* Alt hesaplar (girintili) */}
                {subList.map((sub, sIdx) => (
                  <Box
                    key={sub.id || `sub-${sIdx}`}
                    sx={{ pl: 4, pr: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, bgcolor: 'action.hover' }}
                  >
                    <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">└</Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sub.accountName || sub.accountNumber || '-'}
                        </Typography>
                        {(sub.accountNumber || sub.iban) && sub.accountName && (
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {sub.accountNumber || sub.iban}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color={sub.balance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                    >
                      {formatBalance(sub.balance, sub.currency || 'TRY')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })}
          {/* Toplam */}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.selected' }}>
            <Typography variant="body2" fontWeight="bold">{t('dashboard.total')}</Typography>
            <Typography variant="body2" fontWeight="bold" color={(selectedGroup?.totalBalance || 0) >= 0 ? 'success.main' : 'error.main'}>
              {selectedGroup ? formatBalance(selectedGroup.totalBalance, selectedGroup.currency) : ''}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </StyledCard>
  );
};

export default BankAccountListWidget;
